import React, { useEffect, useState } from "react";
import { useTitle } from "../context/TitleContext";
import PermissionButton from "../components/PermissionButton";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { fetchMessages, deleteMessage, clearMessageState } from "../redux/Slices/MessageSlice";
import { toast } from "react-toastify";
import AddMessage from "../components/Messages/AddMessage";
import EditMessage from "../components/Messages/EditMessage";

/* ── type config ────────────────────────────────────────── */
const TYPE_MAP = {
  guest:        { label: "زائر",        emoji: "👤", bg: "#f1f5f9", color: "#475569" },
  first_login:  { label: "أول دخول",    emoji: "🎉", bg: "#d1fae5", color: "#065f46" },
  repeat_login: { label: "دخول متكرر",  emoji: "🔄", bg: "#dbeafe", color: "#1e40af" },
  inactive:     { label: "غير نشط",     emoji: "😴", bg: "#fef3c7", color: "#92400e" },
};

const TypeBadge = ({ type }) => {
  const cfg = TYPE_MAP[type] || { label: type, emoji: "📝", bg: "#f1f5f9", color: "#64748b" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, background: cfg.bg, color: cfg.color, fontSize: 12, fontWeight: 700 }}>
      {cfg.emoji} {cfg.label}
    </span>
  );
};

const formatDate = (ds) => {
  if (!ds) return "—";
  return new Date(ds).toLocaleString("ar-EG", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

/* ── shared pagination ──────────────────────────────────── */
const PaginationBar = ({ meta, currentPage, loading, onChange }) => {
  if (!meta || meta.last_page <= 1) return null;
  const s = Math.max(1, currentPage - 2), e = Math.min(meta.last_page, currentPage + 2);
  const pages = [];
  for (let i = s; i <= e; i++) {
    pages.push(
      <button key={i} onClick={() => i !== currentPage && onChange(i)} disabled={loading}
        style={{ width: 36, height: 36, borderRadius: 8, border: "none", fontWeight: i === currentPage ? 700 : 400,
          background: i === currentPage ? "var(--main-color)" : "#f1f5f9", color: i === currentPage ? "#fff" : "#475569", cursor: "pointer" }}>
        {i}
      </button>
    );
  }
  const navBtn = (disabled, label, onClick) => (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: "0 14px", height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: disabled ? "#cbd5e1" : "#475569", cursor: disabled ? "not-allowed" : "pointer", fontWeight: 500 }}>
      {label}
    </button>
  );
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 20 }}>
      {navBtn(currentPage === 1 || loading, "السابق", () => onChange(currentPage - 1))}
      <div style={{ display: "flex", gap: 4 }}>{pages}</div>
      {navBtn(currentPage === meta.last_page || loading, "التالي", () => onChange(currentPage + 1))}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Main
══════════════════════════════════════════════════════════ */
const Messages = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();

  const { data: messages, meta, loading, error, deleteLoading } = useSelector((s) => s.messages);

  const [showAddForm, setShowAddForm]       = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [currentPage, setCurrentPage]       = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState(null);

  useEffect(() => { setTitle("رسائل الإشعارات"); dispatch(clearMessageState()); }, [setTitle, dispatch]);
  useEffect(() => { dispatch(fetchMessages(currentPage)); }, [dispatch, currentPage]);
  useEffect(() => { if (error) { toast.error(error); dispatch(clearMessageState()); } }, [error, dispatch]);

  const handleDelete  = (m) => { setDeletingMessage(m); setShowDeleteModal(true); };
  const confirmDelete = () => { if (deletingMessage) { dispatch(deleteMessage(deletingMessage.id)); setShowDeleteModal(false); setDeletingMessage(null); } };
  const handleEdit    = (m) => { dispatch(clearMessageState()); setEditingMessage(m); setShowAddForm(false); };

  /* overlay/modal helpers */
  const overlay  = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
  const modalBox = { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" };

  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            💬 رسائل الإشعارات
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>
            رسائل تُرسل للمستخدمين تلقائياً حسب حالتهم
          </p>
        </div>
        <PermissionButton
          permission="create-messages"
          onClick={() => { setShowAddForm((p) => !p); setEditingMessage(null); }}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: showAddForm ? "#f1f5f9" : "linear-gradient(135deg,var(--main-color),var(--main-light))", color: showAddForm ? "#475569" : "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          {showAddForm ? "✕ إخفاء النموذج" : "＋ إضافة رسالة"}
        </PermissionButton>
      </div>

      {/* ── Add Form ─────────────────────────────────── */}
      {showAddForm && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px 22px", marginBottom: 20, boxShadow: "0 2px 12px rgba(99,102,241,.08)" }}>
          <div style={{ fontWeight: 700, color: "var(--main-color)", fontSize: 15, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            ＋ إضافة رسالة جديدة
          </div>
          <AddMessage onSuccess={() => setShowAddForm(false)} />
        </div>
      )}

      {/* ── Edit Form ─────────────────────────────────── */}
      {editingMessage && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid var(--warning-color)", padding: "20px 22px", marginBottom: 20, boxShadow: "0 2px 12px rgba(251,191,36,.12)" }}>
          <div style={{ fontWeight: 700, color: "var(--warning-color)", fontSize: 15, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <FaEdit /> تعديل رسالة
          </div>
          <EditMessage message={editingMessage} onSuccess={() => setEditingMessage(null)} />
        </div>
      )}

      {/* ── Loading ───────────────────────────────────── */}
      {loading && !messages?.length && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: 72, borderRadius: 12, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Empty ─────────────────────────────────────── */}
      {!loading && messages?.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 52 }}>💬</div>
          <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد رسائل</p>
        </div>
      )}

      {/* ── Cards ─────────────────────────────────────── */}
      {!loading && messages?.length > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {messages.map((msg, idx) => (
              <div key={msg.id}
                style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", alignItems: "flex-start", gap: 14, transition: "box-shadow .2s", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,.12)")}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.04)")}
              >
                {/* index circle */}
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#94a3b8", fontWeight: 700, flexShrink: 0, marginTop: 2 }}>
                  {(meta?.current_page - 1) * (meta?.per_page || 10) + idx + 1}
                </div>

                {/* content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 5 }}>
                    <TypeBadge type={msg.type} />
                    <span style={{ color: "#94a3b8", fontSize: 11 }}>{formatDate(msg.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 14, color: "#334155", fontWeight: 600, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                    {msg.content_ar}
                  </div>
                  {msg.content_en && (
                    <div style={{ fontSize: 12, color: "#94a3b8", direction: "ltr", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", marginTop: 2 }}>
                      {msg.content_en}
                    </div>
                  )}
                </div>

                {/* actions */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <PermissionButton permission="edit-messages" onClick={() => handleEdit(msg)} title="تعديل"
                    style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                    <FaEdit size={14} />
                  </PermissionButton>
                  <PermissionButton permission="delete-messages" onClick={() => handleDelete(msg)} disabled={deleteLoading} title="حذف"
                    style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                    <FaTrash size={14} />
                  </PermissionButton>
                </div>
              </div>
            ))}
          </div>
          <PaginationBar meta={meta} currentPage={currentPage} loading={loading} onChange={(p) => { if (!loading && p !== currentPage) setCurrentPage(p); }} />
        </>
      )}

      {/* ── Delete Modal ──────────────────────────────── */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={modalBox}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingMessage(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف الرسالة؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--main-color)" }}>
                {deletingMessage?.content_ar}
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingMessage(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} disabled={deleteLoading} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {deleteLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري الحذف...</> : "حذف الرسالة"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;